import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'

/** Una fila de la sección Grandes Empresas. */
export interface FilaGE {
  contactId: string
  nombre: string
  cif: string
  poblacion: string
  telefono: string
  ultimoReparto: string | null
  recurrenciaDias: number | null
}

interface RespuestaGE {
  items: FilaGE[]
  total: number
}

export type OrdenGE = 'ultimo_reparto' | 'poblacion'

/** Lista de clientes GE, ordenable y con búsqueda por nombre/CIF/población. */
export function useGrandesEmpresas(orden: OrdenGE = 'ultimo_reparto', busqueda = '') {
  const api = useApi()
  const q = busqueda.trim()
  return useQuery<RespuestaGE, ErrorApi>({
    queryKey: ['ge', orden, q],
    queryFn: ({ signal }) =>
      api.pedir<RespuestaGE>('/api/ge', { senal: signal, parametros: { orden, q: q || undefined } }),
  })
}

/** Fija la recurrencia (días>0 o null) de un GE, con pedido fijo y día opcionales. */
export function useFijarRecurrencia() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<
    FilaGE,
    ErrorApi,
    { contactId: string; dias: number | null; pedidoFijo?: LineaPedidoGE[] | null; diaReparto?: number | null }
  >({
    mutationFn: ({ contactId, dias, pedidoFijo, diaReparto }) =>
      api.pedir<FilaGE>(`/api/ge/${contactId}/recurrencia`, {
        metodo: 'POST',
        cuerpo: { dias, pedidoFijo, diaReparto },
      }),
    onSuccess: (_d, { contactId }) => {
      void qc.invalidateQueries({ queryKey: ['ge'] })
      void qc.invalidateQueries({ queryKey: ['ge', 'config', contactId] })
    },
  })
}

/** Configuración de recurrencia actual de un GE (para rellenar el modal). */
export interface ConfigGE {
  contactId: string
  recurrenciaDias: number | null
  diaReparto: number | null
  pedidoFijo: LineaPedidoGE[] | null
  ultimaGeneracion: string | null
}

export function useConfigGE(contactId: string | null) {
  const api = useApi()
  return useQuery<ConfigGE, ErrorApi>({
    queryKey: ['ge', 'config', contactId],
    enabled: contactId !== null,
    queryFn: ({ signal }) =>
      api.pedir<ConfigGE>(`/api/ge/${contactId}/config`, { senal: signal }),
  })
}

/** Recurrencia sugerida de un GE según sus últimos albaranes. */
export interface SugerenciaRecurrencia {
  dias: number | null
  intervaloMedio: number | null
  muestras: number
}

export function useSugerenciaRecurrencia(contactId: string | null) {
  const api = useApi()
  return useQuery<SugerenciaRecurrencia, ErrorApi>({
    queryKey: ['ge', 'sugerencia', contactId],
    enabled: contactId !== null,
    queryFn: ({ signal }) =>
      api.pedir<SugerenciaRecurrencia>(`/api/ge/${contactId}/sugerencia`, { senal: signal }),
  })
}

/** Un producto del catálogo para GE (sin bonos). */
export interface ProductoGE {
  id: number | string
  nombre: string
  precio: number
  precioConIva: number
}

/** Catálogo que se le ofrece a un GE (todo menos bonos). */
export function useCatalogoGE(habilitado: boolean) {
  const api = useApi()
  return useQuery<{ items: ProductoGE[]; total: number }, ErrorApi>({
    queryKey: ['ge', 'catalogo'],
    enabled: habilitado,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.pedir<{ items: ProductoGE[]; total: number }>('/api/ge/catalogo/sin-bonos', { senal: signal }),
  })
}

/** Línea de un pedido que se agenda para un GE. */
export interface LineaPedidoGE {
  nombre: string
  cantidad: number
  precio: number
  product_id?: number | string | null
}

/** Agenda un pedido para un GE. */
export function useAgendarGE() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string; fecha: string; lineas: LineaPedidoGE[] }>({
    mutationFn: ({ contactId, fecha, lineas }) =>
      api.pedir(`/api/ge/${contactId}/agendar`, {
        metodo: 'POST',
        cuerpo: { fecha, lineas },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ge'] })
    },
  })
}
